import {ChevronDownIcon} from '@sanity/icons/ChevronDown'
import {useImperativeHandle, useRef} from 'react'
import {styled} from 'styled-components'

import {useCustomValidity} from '../../hooks/useCustomValidity'
import {_getArrayProp} from '../../styles/helpers'
import {Radius} from '../../types/radius'
import {Box} from '../box/box'
import {Text} from '../text/text'
import {selectStyle} from './styles'

import {select} from './select.css'

/**
 * @public
 */
export interface SelectProps {
  fontSize?: number | number[]
  gap?: number | number[]
  padding?: number | number[]
  radius?: Radius | Radius[]
  /**
   * @deprecated Use `gap` instead.
   */
  space?: never
  customValidity?: string
}

const Input = styled.select<{
  $fontSize: number[]
  $padding: number[]
  $radius: Radius[]
  $space: number[]
}>(selectStyle.input)

const IconBox = styled(Box)(selectStyle.iconBox)

/**
 * The `Select` component provides control of options.
 *
 * @public
 */
export function Select(props: SelectProps & Omit<React.HTMLProps<HTMLSelectElement>, 'as'>) {
  const {
    children,
    customValidity,
    disabled,
    fontSize = 2,
    gap = 3,
    padding = 3,
    radius = 2,
    readOnly,
    ref: forwardedRef,
    ...restProps
  } = props

  const ref = useRef<HTMLSelectElement | null>(null)

  useImperativeHandle<HTMLSelectElement | null, HTMLSelectElement | null>(
    forwardedRef,
    () => ref.current,
  )

  useCustomValidity(ref, customValidity)

  return (
    <div className={select} data-ui="Select">
      <Input
        data-read-only={!disabled && readOnly ? '' : undefined}
        data-ui="Select"
        {...restProps}
        $fontSize={_getArrayProp(fontSize)}
        $padding={_getArrayProp(padding)}
        $radius={_getArrayProp(radius)}
        $space={_getArrayProp(gap)}
        disabled={disabled || readOnly}
        ref={ref}
      >
        {children}
      </Input>

      <IconBox padding={padding}>
        <Text size={fontSize}>
          <ChevronDownIcon />
        </Text>
      </IconBox>
    </div>
  )
}
